import { useEffect, useState } from "react";
import { api, formatApiError } from "../api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "../AuthContext";

const empty = { email: "", name: "", password: "", role: "editor" };

export default function UsersAdmin() {
  const { user: me } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/api/auth/users");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(empty); setEditing(false); setOpen(true); };
  const startEdit = (u) => { setForm({ ...u, password: "" }); setEditing(true); setOpen(true); };

  const remove = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/api/auth/users/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, status: form.status };
        if (form.password) payload.password = form.password;
        const { data } = await api.put(`/api/auth/users/${form.id}`, payload);
        setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
      } else {
        const { data } = await api.post("/api/auth/users", {
          email: form.email,
          name: form.name,
          password: form.password,
          role: form.role,
        });
        setItems((arr) => [data, ...arr]);
      }
      setOpen(false);
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-serif font-bold">Users</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage who can sign in to the admin panel.</p>
        </div>
        <Button onClick={startNew} className="bg-primary hover:bg-primary-dark text-white" data-testid="new-user-btn">
          <Plus className="w-4 h-4 mr-2" /> New user
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Role</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-t" data-testid={`user-row-${u.id}`}>
                  <td className="p-3 font-medium">{u.name}</td>
                  <td className="p-3">{u.email}</td>
                  <td className="p-3"><span className="text-xs px-2 py-0.5 rounded-full bg-muted">{u.role}</span></td>
                  <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full ${u.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}`}>{u.status}</span></td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => startEdit(u)}><Pencil className="w-3 h-3" /></Button>
                    {u.id !== me?.id && (
                      <Button size="sm" variant="outline" className="text-destructive ml-1" onClick={() => remove(u.id)}><Trash2 className="w-3 h-3" /></Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit user" : "New user"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Email *</Label><Input type="email" disabled={editing} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>{editing ? "New password (leave blank to keep)" : "Password * (min 8 chars)"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div><Label>Role</Label>
              <select className="w-full h-10 px-3 rounded-md border border-input bg-background" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                <option value="editor">Editor</option>
                <option value="admin">Admin</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1">Editors can manage content. Admins also manage users and site settings.</p>
            </div>
            {editing && (
              <div><Label>Status</Label>
                <select className="w-full h-10 px-3 rounded-md border border-input bg-background" value={form.status || "active"} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary-dark text-white">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
